/* ════ ANNUAL LEAVE ════════════════════════════════════════ */
// Request → approve → planner. Balances are never stored: they're always
// worked out as allowance − approved days, so cancelling or changing leave
// corrects itself. Who can see and change what is enforced in the database
// (migration 0025) — this module only mirrors those rules in the UI.
//
// Leave year: Jan–Dec. Weekends and England & Wales bank holidays don't count.
import { state } from './state.js';
import { $, $$ } from './dom.js';
import { MO, LEAVE_PENDING_NAME } from './config.js';
import { sb } from './supabase/client.js';
import { setSyncDot, norm } from './supabase/data.js';
import { showToast } from './utils/toast.js';
import { confetti, confettiFrom } from './utils/confetti.js';
import { levelUp } from './utils/chime.js';
import { pdate, fdate, addD, isWE } from './utils/date.js';
import { displayName } from './utils/name.js';
import { isBankHoliday, bankHolidayName, loadBankHolidays } from './bankholidays.js';
import { buildGantt } from './gantt/index.js';
import { renderRecordsTab, myRecordHTML, initRecords } from './records.js';

const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const lc = s => String(s || '').toLowerCase();
const me = () => lc(state.currentUser?.email);
const todayISO = () => fdate(new Date());
const thisYear = () => new Date().getFullYear();
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const fmtDays = n => String(Math.round(n * 2) / 2);
const dayWord = n => (n === 1 ? 'day' : 'days');

export const isApprover = () => !!state.settings.leave_approver && lc(state.settings.leave_approver) === me();
// HR viewers (e.g. Will) see everyone's leave and records but can't change anything.
export const isViewer = () => String(state.settings.leave_viewers || '').toLowerCase().replace(/\s/g, '').split(',').includes(me());
export const isHR = () => isApprover() || isViewer();
export const resFor = email => state.resources.find(r => lc(r.user_email) === lc(email));
export const nameFor = email => resFor(email)?.name || displayName(email);
const firstName = email => nameFor(email).split(' ')[0];
const approverName = () => (state.settings.leave_approver ? firstName(state.settings.leave_approver) : 'your approver');

export function fmtRange(s, e) {
  const a = pdate(s), b = pdate(e);
  if (s === e) return `${DOW[a.getDay()]} ${a.getDate()} ${MO[a.getMonth()]} ${a.getFullYear()}`;
  if (a.getFullYear() !== b.getFullYear()) return `${a.getDate()} ${MO[a.getMonth()]} ${a.getFullYear()} – ${b.getDate()} ${MO[b.getMonth()]} ${b.getFullYear()}`;
  if (a.getMonth() !== b.getMonth()) return `${a.getDate()} ${MO[a.getMonth()]} – ${b.getDate()} ${MO[b.getMonth()]} ${b.getFullYear()}`;
  return `${a.getDate()}–${b.getDate()} ${MO[a.getMonth()]} ${a.getFullYear()}`;
}
const HALF_NOTE = { pm: 'starts in the afternoon', am: 'ends at lunchtime', pmam: 'starts in the afternoon, ends at lunchtime' };

/* ── Day maths ──────────────────────────────────────────── */
// Working days in [start, end]: weekdays that aren't bank holidays, with half
// days taken off the first/last day. `keep(iso)` optionally limits which dates
// count (e.g. only one leave year, or only past dates).
export function leaveDays(start, end, half = 'none', keep = null) {
  let n = 0;
  for (let d = pdate(start), e = pdate(end); d <= e; d = addD(d, 1)) {
    const iso = fdate(d);
    if (isWE(d) || isBankHoliday(iso) || (keep && !keep(iso))) continue;
    let v = 1;
    if (iso === start && (half === 'pm' || half === 'pmam')) v -= 0.5;
    if (iso === end && (half === 'am' || half === 'pmam')) v -= 0.5;
    n += Math.max(0, v);
  }
  return n;
}
const inYear = y => iso => iso.slice(0, 4) === String(y);
const yearsOf = (s, e) => { const out = []; for (let y = +s.slice(0, 4); y <= +e.slice(0, 4); y++) out.push(y); return out; };
function weekdaysIn(s, e) { let n = 0; for (let d = pdate(s), x = pdate(e); d <= x; d = addD(d, 1)) if (!isWE(d)) n++; return n; }
function bankHolsIn(s, e) {
  const out = [];
  for (let d = pdate(s), x = pdate(e); d <= x; d = addD(d, 1)) { const iso = fdate(d); if (!isWE(d) && isBankHoliday(iso)) out.push(iso); }
  return out;
}

// Allowance, taken (before today), booked (today on), pending and left for one person and year.
export function leaveSummary(email, year) {
  const al = state.leaveAllowances.find(a => lc(a.user_email) === lc(email) && Number(a.year) === year);
  const allowance = al ? Number(al.days) : null;
  const t = todayISO(), y = inYear(year);
  let taken = 0, booked = 0, pending = 0;
  state.leaveRequests.filter(r => lc(r.user_email) === lc(email)).forEach(r => {
    if (r.status === 'pending') pending += leaveDays(r.start, r.end, r.half, y);
    if (r.status === 'approved') {
      taken += leaveDays(r.start, r.end, r.half, iso => y(iso) && iso < t);
      booked += leaveDays(r.start, r.end, r.half, iso => y(iso) && iso >= t);
    }
  });
  // Annual leave HR recorded directly (e.g. taken before GRANTT) counts like approved leave.
  (state.absences || []).filter(a => a.kind === 'annual' && lc(a.user_email) === lc(email) && y(a.start)).forEach(a => {
    if (a.start < t) taken += Number(a.days || 0); else booked += Number(a.days || 0);
  });
  // Leave adjustments (extra days granted, carry-over…) move the balance either way.
  const adjust = (state.absences || []).filter(a => a.kind === 'adjust' && lc(a.user_email) === lc(email) && y(a.start))
    .reduce((n, a) => n + Number(a.days || 0), 0);
  // Bank holidays come off the allowance (Vanessa's rule; switchable), counting
  // only those on or after the person's start date in that year.
  let bhTaken = 0, bhBooked = 0;
  if (allowance != null && bhDeducted()) {
    bankHolsIn(bhFrom(al, year), `${year}-12-31`).forEach(iso => { if (iso < t) bhTaken++; else bhBooked++; });
  }
  const bh = bhTaken + bhBooked;
  return { allowance, adjust, taken, booked, pending, bh, bhTaken, bhBooked,
    left: allowance == null ? null : allowance + adjust - taken - booked - bh };
}
// Whether bank holidays come off the allowance: on unless the approver turns it off.
export const bhDeducted = () => state.settings.leave_bh_deduct !== 'off';
// First date bank holidays count from: the pro-rata start date in that year, else 1 Jan.
export const bhFrom = (al, year) => (al?.start_date && al.start_date.slice(0, 4) === String(year) ? al.start_date : `${year}-01-01`);

// Other people already off (approved leave / OOO blocks) during [s, e].
function clashes(s, e, email) {
  const mine = resFor(email)?.id;
  const out = [];
  state.tasks.filter(t => t.cat === 'ooo' && t.resource !== mine && t.start <= e && t.end >= s).forEach(t => {
    const who = state.resources.find(r => r.id === t.resource)?.name || 'Someone';
    out.push(`${who} is also off ${fmtRange(t.start > s ? t.start : s, t.end < e ? t.end : e)}${t.name === LEAVE_PENDING_NAME ? ' (not approved yet)' : ''}`);
  });
  return [...new Set(out)];
}

/* ── Avatar menu: my balance ───────────────────────────── */
function renderMenu() {
  if (!$('um-leave')) return;
  const y = thisYear(), s = leaveSummary(me(), y);
  $('um-leave-year').textContent = y;
  if (s.allowance == null) {
    $('um-bal').innerHTML = '<span class="um-bal-none">Allowance not set yet</span>';
    $('um-bar').innerHTML = ''; $('um-bal-sub').textContent = '';
  } else {
    $('um-bal').innerHTML = `<b>${fmtDays(s.left)}</b> of ${fmtDays(s.allowance)} days left`;
    const pct = v => (s.allowance ? Math.max(0, Math.min(100, v / s.allowance * 100)) : 0);
    $('um-bar').innerHTML = `<span class="tk" style="width:${pct(s.taken)}%"></span><span class="bk" style="width:${pct(s.booked)}%"></span>`;
    $('um-bal-sub').textContent = `${fmtDays(s.taken)} taken · ${fmtDays(s.booked)} booked${s.bh ? ` · ${s.bh} bank ${s.bh === 1 ? 'holiday' : 'holidays'}` : ''}${s.pending ? ` · ${fmtDays(s.pending)} pending` : ''}`;
  }
  // Next year too, so people booking ahead can see what they have.
  const ny = y + 1, n = leaveSummary(me(), ny), nx = $('um-next');
  if (nx) {
    const used = n.booked + n.taken;
    nx.innerHTML = n.allowance == null
      ? `<b>${ny}</b> <span>allowance not set yet${used || n.pending ? ` · ${fmtDays(used + n.pending)} requested` : ''}</span>`
      : `<b>${ny}</b> <span><b class="${n.left < 0 ? 'neg' : ''}">${fmtDays(n.left)}</b> of ${fmtDays(n.allowance)} left${used ? ` · ${fmtDays(used)} booked` : ''}${n.pending ? ` · ${fmtDays(n.pending)} pending` : ''}</span>`;
  }
  const pend = state.leaveRequests.filter(r => lc(r.user_email) === me() && r.status === 'pending').length;
  $('btn-leave-mine').textContent = pend ? `🗓️ My leave (${pend} pending)` : '🗓️ My leave';
}

/* ── Request form ──────────────────────────────────────── */
function openRequest(pre = null) {
  $('user-menu').classList.remove('open'); $('myleave-modal').classList.remove('open');
  if (pre instanceof Event) pre = null;
  $('lr-start').value = pre?.start || ''; $('lr-end').value = pre?.end || ''; $('lr-sh').value = 'full'; $('lr-eh').value = 'full'; $('lr-note').value = pre?.note || '';
  $('lr-approver').textContent = approverName();
  renderCalc();
  $('leave-modal').classList.add('open');
  setTimeout(() => $('lr-start').focus(), 200);
}
const closeRequest = () => $('leave-modal').classList.remove('open');

function readForm() {
  const start = $('lr-start').value, end = $('lr-end').value || start;
  const pm = $('lr-sh').value === 'pm', am = $('lr-eh').value === 'am';
  return { start, end, half: pm && am ? 'pmam' : pm ? 'pm' : am ? 'am' : 'none', note: $('lr-note').value.trim() };
}

// Live day count + balance. Returns the request (or null if not sendable).
function renderCalc() {
  const box = $('lr-calc'), f = readForm();
  if (!f.start) { box.innerHTML = '<div class="lc-empty">Pick your dates to see how many days this uses.</div>'; return null; }
  if (f.end < f.start) { box.innerHTML = '<div class="lc-err">The end date is before the start date.</div>'; return null; }
  const days = leaveDays(f.start, f.end, f.half);
  if (!days) { box.innerHTML = '<div class="lc-err">Those dates are all weekends or bank holidays, so there\'s nothing to book.</div>'; return null; }

  const bh = bankHolsIn(f.start, f.end), wd = weekdaysIn(f.start, f.end);
  let breakdown = `${wd} ${wd === 1 ? 'weekday' : 'weekdays'}`;
  if (bh.length) breakdown += ` − ${bh.length} bank ${bh.length === 1 ? 'holiday' : 'holidays'}${bhDeducted() ? ' (already counted in your balance)' : ''}`;
  if (HALF_NOTE[f.half]) breakdown += ` · ${HALF_NOTE[f.half]}`;

  let html = `<div class="lc-top"><span class="lc-big">${fmtDays(days)}</span><span class="lc-lab">${dayWord(days)} of annual leave</span></div>`
    + `<div class="lc-sum">${breakdown}</div>`;
  const years = yearsOf(f.start, f.end).filter(y => leaveDays(f.start, f.end, f.half, inYear(y)));
  const warns = [];
  years.forEach(y => {
    const d = leaveDays(f.start, f.end, f.half, inYear(y)), s = leaveSummary(me(), y);
    const split = years.length > 1 ? ` <span class="lc-muted">(${fmtDays(d)} in ${y})</span>` : '';
    if (s.allowance == null) { html += `<div class="lc-bal">${y}: allowance not set yet${split}</div>`; return; }
    const after = s.left - d;
    html += `<div class="lc-bal">${y} balance <b>${fmtDays(s.left)}</b> → <b class="${after < 0 ? 'neg' : 'pos'}">${fmtDays(after)}</b> of ${fmtDays(s.allowance)}${split}</div>`;
    if (after < 0) warns.push(`This is ${fmtDays(-after)} ${dayWord(-after)} more than you have left in ${y}. ${approverName()} will see that.`);
  });
  if (bh.length) html += `<div class="lc-chips">${bh.map(iso => `<span class="lc-chip">${pdate(iso).getDate()} ${MO[pdate(iso).getMonth()]} · ${esc(bankHolidayName(iso))}</span>`).join('')}</div>`;
  clashes(f.start, f.end, me()).forEach(c => warns.push(`${c}.`));
  const overlap = state.leaveRequests.find(r => lc(r.user_email) === me() && (r.status === 'pending' || r.status === 'approved') && r.start <= f.end && r.end >= f.start);
  warns.forEach(w => { html += `<div class="lc-warn">⚠️ ${esc(w)}</div>`; });
  if (overlap) html += `<div class="lc-err">You already have leave ${overlap.status === 'pending' ? 'requested' : 'booked'} on some of these dates (${fmtRange(overlap.start, overlap.end)}).</div>`;
  box.innerHTML = html;
  return overlap ? null : { ...f, days };
}

async function sendRequest() {
  const c = renderCalc(); if (!c) { showToast('Check your dates first'); return; }
  const btn = $('lr-send'); btn.disabled = true;
  setSyncDot('busy');
  const lane = resFor(me());
  // A locked, tentative block on my lane so the team can see it while it's pending.
  let block = null;
  if (lane) {
    const { data: t } = await sb.from('tasks').insert({
      name: LEAVE_PENDING_NAME, resource: lane.id, start: c.start, end: c.end, half: c.half, cat: 'ooo',
      info: '', importance: 'normal', created_by: state.currentUser.email, locked: true, tags: [], subtasks: [],
    }).select().single();
    if (t) block = norm(t);
  }
  const row = {
    user_email: state.currentUser.email, resource_id: lane?.id || null,
    start: c.start, end: c.end, half: c.half, days: c.days, note: c.note || null, status: 'pending',
    task_id: block ? String(block.id) : null,
  };
  const { data, error } = await sb.from('leave_requests').insert(row).select().single();
  if (error) {
    btn.disabled = false;
    if (block) await sb.from('tasks').delete().eq('id', block.id);   // no request, no block
    setSyncDot('err');
    showToast(/does not exist|42P01/.test(`${error.code} ${error.message}`) ? 'Leave isn\'t switched on yet. Run migration 0025.' : 'Could not send: ' + error.message);
    return;
  }
  if (data && !state.leaveRequests.find(r => r.id === data.id)) state.leaveRequests.push(data);
  if (block && !state.tasks.find(t => String(t.id) === String(block.id))) state.tasks.push(block);
  if (block) buildGantt();
  // Celebrate: the button confirms, confetti bursts from it, then the form closes.
  const label = btn.textContent;
  btn.textContent = `✓ Sent to ${approverName()}`; btn.classList.add('is-sent');
  confettiFrom(btn, { emoji: ['🌴', '☀️', '✈️', '🏖️'] });
  setSyncDot('ok');
  setTimeout(() => { closeRequest(); btn.textContent = label; btn.classList.remove('is-sent'); btn.disabled = false; }, 900);
  showToast(`🌴 Request sent to ${approverName()}`);
  renderLeaveUI();
  if (data?.id) notifyLeave(data.id, 'requested');
}

// Email the other side (approver ↔ requester) via the 'notify-leave' Edge Function.
// Best-effort: the leave action has already succeeded, so a mail hiccup is only logged.
async function notifyLeave(id, event) {
  try {
    const { data, error } = await sb.functions.invoke('notify-leave', { body: { id, event } });
    if (error || data?.ok === false) console.warn('Leave email not sent:', error?.message || data?.error);
  } catch (e) { console.warn('Leave email not sent:', e); }
}

/* ── My leave ──────────────────────────────────────────── */
const STATUS = { pending: 'Waiting for approval', approved: 'Approved', denied: 'Declined', cancelled: 'Cancelled' };

function openMine() { $('user-menu').classList.remove('open'); renderMine(); $('myleave-modal').classList.add('open'); markSeen(); renderLeaveUI(); }

function summaryCard(email, y) {
  const s = leaveSummary(email, y);
  if (s.allowance == null) return `<div class="ml-sum"><div class="ml-sum-y">${y}</div><div class="lc-muted">Allowance not set yet</div></div>`;
  return `<div class="ml-sum"><div class="ml-sum-y">${y}</div>
    <div class="ml-stats">
      <div><b>${fmtDays(s.allowance)}</b><span>allowance</span></div>
      <div><b>${fmtDays(s.taken)}</b><span>taken</span></div>
      <div><b>${fmtDays(s.booked)}</b><span>booked</span></div>
      ${s.bh ? `<div><b>${s.bh}</b><span>bank hols</span></div>` : ''}
      <div><b>${fmtDays(s.pending)}</b><span>pending</span></div>
      <div class="ml-left"><b>${fmtDays(s.left)}</b><span>left</span></div>
    </div></div>`;
}

let mineTab = 'requests', mineYear = new Date().getFullYear();
function renderMine() {
  const box = $('ml-body'); if (!box) return;
  const y0 = thisYear();
  const tabs = `<div class="ml-tabs"><button class="lv-tab${mineTab === 'requests' ? ' on' : ''}" data-mt="requests">Requests</button>
    <button class="lv-tab${mineTab === 'record' ? ' on' : ''}" data-mt="record">My record</button>
    ${mineTab === 'record' ? `<span class="rc-spacer"></span>${[y0, y0 + 1].map(y => `<button class="lv-tab sm${mineYear === y ? ' on' : ''}" data-my="${y}">${y}</button>`).join('')}` : ''}</div>`;
  if (mineTab === 'record') { box.innerHTML = tabs + myRecordHTML(mineYear) + `<div class="rc-foot">Your annual leave, bank holidays${bhDeducted() ? '' : ' (not deducted)'}, time in lieu and balance. Only you and HR can see this.</div>`; return; }
  const mine = state.leaveRequests.filter(r => lc(r.user_email) === me()).sort((a, b) => b.start.localeCompare(a.start));
  const years = new Set([thisYear(), thisYear() + 1]);
  mine.forEach(r => { if (r.status !== 'denied' && r.status !== 'cancelled') yearsOf(r.start, r.end).forEach(y => years.add(y)); });
  const t = todayISO();
  box.innerHTML = tabs + [...years].sort().map(y => summaryCard(me(), y)).join('')
    + (mine.length ? mine.map(r => {
      const canCancel = (r.status === 'pending' || r.status === 'approved') && r.end >= t;
      return `<div class="ml-row st-${r.status}">
        <div class="ml-main"><div class="ml-dates">${fmtRange(r.start, r.end)}</div>
          <div class="ml-meta">${fmtDays(Number(r.days))} ${dayWord(Number(r.days))}${HALF_NOTE[r.half] ? ` · ${HALF_NOTE[r.half]}` : ''}${r.note ? ` · “${esc(r.note)}”` : ''}</div>
          ${r.status === 'denied' && r.decision_note ? `<div class="ml-reason">Reason: ${esc(r.decision_note)}</div>` : ''}
          ${r.status === 'denied' && r.proposal_start ? `<div class="ml-proposal">Suggested instead: <b>${fmtRange(r.proposal_start, r.proposal_end || r.proposal_start)}</b>
            <button class="lv-btn ok" data-rerequest="${r.id}">Request these dates</button></div>` : ''}</div>
        <span class="lv-pill p-${r.status}">${STATUS[r.status]}</span>
        ${canCancel ? `<button class="lv-btn no" data-cancel="${r.id}">Cancel</button>` : ''}
      </div>`;
    }).join('') : '<div class="list-empty">No leave requested yet.</div>');
}

async function cancelMine(id) {
  const r = state.leaveRequests.find(x => x.id === id); if (!r) return;
  const extra = r.status === 'approved' ? ' The days go back on your balance and it comes off the planner.' : '';
  if (!confirm(`Cancel your leave for ${fmtRange(r.start, r.end)}?${extra}`)) return;
  setSyncDot('busy');
  const { error } = await sb.from('leave_requests').update({ status: 'cancelled' }).eq('id', id);
  if (error) { setSyncDot('err'); showToast('Could not cancel: ' + error.message); return; }
  if (r.task_id) {
    await sb.from('tasks').delete().eq('id', r.task_id);
    state.tasks = state.tasks.filter(t => String(t.id) !== String(r.task_id)); buildGantt();
  }
  r.status = 'cancelled';
  setSyncDot('ok'); showToast('Leave cancelled'); renderLeaveUI();
  notifyLeave(id, 'cancelled');
}

/* ── Approver: Leave view ──────────────────────────────── */
export function people() {
  const m = new Map();
  const add = e => { if (e) m.set(lc(e), e); };
  state.resources.forEach(r => add(r.user_email));
  state.leaveAllowances.forEach(a => add(a.user_email));
  state.leaveRequests.forEach(r => add(r.user_email));
  return [...m.values()].sort((a, b) => nameFor(a).localeCompare(nameFor(b)));
}
export function avatar(email) {
  const r = resFor(email);
  const ini = r?.ini || nameFor(email).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return `<span class="lv-av" style="background:${r?.col || '#9CA3AF'}">${esc(ini)}</span>`;
}

function requestCard(r) {
  const tags = [];
  yearsOf(r.start, r.end).forEach(y => {
    const d = leaveDays(r.start, r.end, r.half, inYear(y)); if (!d) return;
    const s = leaveSummary(r.user_email, y);
    const left = s.allowance == null ? 'allowance not set' : `balance ${fmtDays(s.left)} → ${fmtDays(s.left - d)} left after`;
    tags.push(`<span class="lv-tag d">${fmtDays(d)} ${dayWord(d)}${yearsOf(r.start, r.end).length > 1 ? ` in ${y}` : ''} · ${left}</span>`);
    if (s.allowance != null && s.left - d < 0) tags.push('<span class="lv-tag w">Over allowance</span>');
  });
  clashes(r.start, r.end, r.user_email).forEach(c => tags.push(`<span class="lv-tag w">${esc(c)}</span>`));
  return `<div class="lv-req">
    ${avatar(r.user_email)}
    <div class="lv-req-main"><div class="lv-who">${esc(nameFor(r.user_email))}</div>
      <div class="lv-dt">${fmtRange(r.start, r.end)}${HALF_NOTE[r.half] ? ` · ${HALF_NOTE[r.half]}` : ''}${r.note ? ` · “${esc(r.note)}”` : ''}</div>
      <div class="lv-tags">${tags.join('')}</div></div>
    ${isApprover() ? `<div class="lv-acts"><button class="lv-btn ok" data-approve="${r.id}">Approve</button><button class="lv-btn no" data-deny="${r.id}">Decline</button></div>`
      : `<div class="lv-acts"><span class="lc-muted">${esc(approverName())} decides</span></div>`}
  </div>`;
}

function renderLeaveView() {
  const body = $('lv-body'); if (!body) return;
  if (!isHR()) { body.innerHTML = '<div class="list-empty">Only HR can see this page.</div>'; return; }
  $$('.lv-tab').forEach(b => b.classList.toggle('on', b.dataset.lt === state.leaveTab));
  const ys = $('lv-year'); ys.style.display = state.leaveTab === 'allowances' || state.leaveTab === 'records' ? '' : 'none';

  if (state.leaveTab === 'requests') {
    const pend = state.leaveRequests.filter(r => r.status === 'pending').sort((a, b) => a.start.localeCompare(b.start));
    body.innerHTML = pend.length ? pend.map(requestCard).join('') : '<div class="list-empty">No leave requests waiting. 🎉</div>';
  } else if (state.leaveTab === 'allowances') {
    renderAllowances(body);
  } else if (state.leaveTab === 'records') {
    renderRecordsTab(body);
  } else {
    const done = state.leaveRequests.filter(r => r.status !== 'pending')
      .sort((a, b) => String(b.decided_at || b.created_at).localeCompare(String(a.decided_at || a.created_at)));
    body.innerHTML = done.length ? done.map(r => `<div class="lv-hist">${avatar(r.user_email)}
      <div class="lv-req-main"><div class="lv-who">${esc(nameFor(r.user_email))} <span class="lc-muted">· ${fmtRange(r.start, r.end)} · ${fmtDays(Number(r.days))} ${dayWord(Number(r.days))}</span></div>
      ${r.decision_note ? `<div class="lv-dt">Reason: ${esc(r.decision_note)}</div>` : ''}</div>
      <span class="lv-pill p-${r.status}">${STATUS[r.status]}</span></div>`).join('') : '<div class="list-empty">No decisions yet.</div>';
  }
}

// Keep the local planner in step without waiting for realtime.
function putTask(row) {
  const t = norm(row), i = state.tasks.findIndex(x => String(x.id) === String(t.id));
  if (i >= 0) state.tasks[i] = t; else state.tasks.push(t);
}
const dropTask = id => { state.tasks = state.tasks.filter(t => String(t.id) !== String(id)); };

async function approve(id) {
  const r = state.leaveRequests.find(x => x.id === id); if (!r) return;
  const res = resFor(r.user_email) || state.resources.find(x => x.id === r.resource_id);
  setSyncDot('busy');
  let taskId = null, promoted = false;
  const firm = { name: 'Annual Leave', start: r.start, end: r.end, half: r.half, cat: 'ooo', locked: false };
  // Turn the pending block into the real one (it may have been deleted meanwhile).
  if (r.task_id) {
    const { data } = await sb.from('tasks').update(firm).eq('id', r.task_id).select();
    if (data?.length) { taskId = String(data[0].id); promoted = true; putTask(data[0]); }
  }
  if (!taskId && res) {
    const { data, error } = await sb.from('tasks').insert({
      ...firm, resource: res.id, info: '', importance: 'normal', created_by: r.user_email, tags: [], subtasks: [],
    }).select().single();
    if (error) { setSyncDot('err'); showToast('Could not add it to the planner: ' + error.message); return; }
    taskId = String(data.id); putTask(data);
  }
  buildGantt();
  const patch = { status: 'approved', decided_by: state.currentUser.email, decided_at: new Date().toISOString(), task_id: taskId };
  const { error } = await sb.from('leave_requests').update(patch).eq('id', id);
  if (error) {
    // Put the planner back how it was: pending again, or no orphan block.
    if (promoted) { const { data } = await sb.from('tasks').update({ name: LEAVE_PENDING_NAME, locked: true }).eq('id', taskId).select(); if (data?.[0]) putTask(data[0]); }
    else if (taskId) { await sb.from('tasks').delete().eq('id', taskId); dropTask(taskId); }
    buildGantt();
    setSyncDot('err'); showToast('Could not approve: ' + error.message); return;
  }
  Object.assign(r, patch);
  setSyncDot('ok');
  showToast(res ? `✅ Approved · added to ${res.name}'s planner` : `✅ Approved · no planner lane is linked to ${nameFor(r.user_email)}`);
  renderLeaveUI();
  notifyLeave(id, 'approved');
}

// Decline: a proper form with the reason (required) and, optionally, other
// dates to propose. The person sees both, and can re-request the proposal in one click.
let declining = null;
function decline(id) {
  const r = state.leaveRequests.find(x => x.id === id); if (!r) return;
  declining = r;
  $('dc-who').textContent = `${nameFor(r.user_email)} · ${fmtRange(r.start, r.end)} · ${fmtDays(Number(r.days))} ${dayWord(Number(r.days))}`;
  $('dc-first').textContent = firstName(r.user_email);
  $('dc-reason').value = ''; $('dc-ps').value = ''; $('dc-pe').value = ''; $('dc-err').textContent = '';
  $('decline-modal').classList.add('open');
  setTimeout(() => $('dc-reason').focus(), 150);
}
async function sendDecline() {
  const r = declining; if (!r) return;
  const reason = $('dc-reason').value.trim(), ps = $('dc-ps').value, pe = $('dc-pe').value || ps;
  if (!reason) { $('dc-err').textContent = `Please explain why, so ${firstName(r.user_email)} knows.`; $('dc-reason').focus(); return; }
  if (ps && pe < ps) { $('dc-err').textContent = 'The suggested end date is before the start date.'; return; }
  const patch = { status: 'denied', decided_by: state.currentUser.email, decided_at: new Date().toISOString(), decision_note: reason,
    proposal_start: ps || null, proposal_end: ps ? pe : null, task_id: null };
  const id = r.id;
  setSyncDot('busy');
  const { error } = await sb.from('leave_requests').update(patch).eq('id', id);
  if (error) { setSyncDot('err'); $('dc-err').textContent = 'Could not decline: ' + error.message; return; }
  $('decline-modal').classList.remove('open'); declining = null;
  if (r.task_id) { await sb.from('tasks').delete().eq('id', r.task_id); dropTask(r.task_id); buildGantt(); }   // remove the pending block
  Object.assign(r, patch); setSyncDot('ok'); showToast(`Declined · ${firstName(r.user_email)} has been told why${ps ? ' and offered other dates' : ''}`); renderLeaveUI();
  notifyLeave(id, 'denied');
}

/* ── Allowances: contract days, pro-rata for joiners ────── */
// Entitlement for a year = contract days, or pro-rata from a start date in
// that year (rounded up to the next half day, like Vanessa's sheet:
// 28 ÷ 365 × 214 = 16.4 → 16.5). Bank holidays never come off.
const daysInYear = y => (new Date(y, 1, 29).getMonth() === 1 ? 366 : 365);
export function proRata(contract, from, year) {
  if (contract == null || contract === '') return null;
  const c = Number(contract);
  if (!from || from.slice(0, 4) !== String(year)) return { days: c, rem: null, raw: null };
  const rem = Math.round((new Date(year, 11, 31) - pdate(from)) / 864e5) + 1;
  const raw = c * rem / daysInYear(year);
  return { days: Math.ceil(raw * 2) / 2, rem, raw };
}
export const allowanceRow = (email, year) => state.leaveAllowances.find(a => lc(a.user_email) === lc(email) && Number(a.year) === Number(year));

function renderAllowances(body) {
  const y = state.leaveYear, ny = y + 1, ppl = people(), edit = isApprover();
  const missingNext = ppl.filter(e => allowanceRow(e, y) && !allowanceRow(e, ny)).length;
  const cells = (email, yr) => {
    const al = allowanceRow(email, yr), s = leaveSummary(email, yr), dis = edit ? '' : ' disabled';
    const legacy = al && al.contract_days == null;
    const pct = s.allowance ? Math.max(0, Math.min(100, s.left / s.allowance * 100)) : 0, low = s.allowance != null && s.left <= 2;
    const pr = al && al.contract_days != null ? proRata(al.contract_days, al.start_date, yr) : null;
    return `<td class="rt${yr === ny ? ' nx' : ''}"><input class="lv-allow" type="number" min="0" step="0.5" data-email="${esc(email)}" data-year="${yr}" data-f="contract" value="${al?.contract_days ?? ''}" placeholder="${legacy ? fmtDays(Number(al.days)) : '—'}" aria-label="${esc(nameFor(email))} contract days ${yr}"${dis}></td>
      <td><input class="lv-from" type="date" min="${yr}-01-01" max="${yr}-12-31" data-email="${esc(email)}" data-year="${yr}" data-f="from" value="${al?.start_date || ''}" aria-label="${esc(nameFor(email))} pro-rata from ${yr}"${dis}></td>
      <td class="rt">${s.allowance == null ? '<span class="lc-muted">–</span>' : `<b>${fmtDays(s.allowance)}</b>${pr?.raw != null ? `<div class="lv-sub">${fmtDays(al.contract_days)} × ${pr.rem}/${daysInYear(yr)}</div>` : legacy ? '<div class="lv-sub">set directly</div>' : ''}`}</td>
      <td class="rt">${fmtDays(s.taken + s.booked + s.bh)}${s.bh ? `<div class="lv-sub">incl. ${s.bh} bank hol${s.bh === 1 ? '' : 's'}</div>` : ''}${s.adjust ? `<div class="lv-sub">${s.adjust > 0 ? '+' : ''}${fmtDays(s.adjust)} adjusted</div>` : ''}</td>
      <td class="rt">${s.allowance == null ? '<span class="lc-muted">not set</span>' : `<span class="lv-left"><span class="lv-bar"><span style="width:${pct}%" class="${low ? 'low' : ''}"></span></span><b class="${low ? 'low' : ''}">${fmtDays(s.left)}</b></span>`}${s.pending ? `<div class="lv-sub">${fmtDays(s.pending)} pending</div>` : ''}</td>`;
  };
  const bhNote = bhDeducted() ? 'Bank holidays come off the allowance (from the start date, if there is one).' : 'Bank holidays don\'t come off the allowance.';
  body.innerHTML = `<div class="lv-hint">${edit ? `Enter each person's contract days (e.g. 28). For someone who joined part-way through a year, add the date they started and it works out the pro-rata. ${bhNote}`
      : `You can see everyone's allowances; only ${esc(approverName())} can change them. ${bhNote}`}
      ${edit ? `<label class="lv-bh"><input type="checkbox" id="lv-bh"${bhDeducted() ? ' checked' : ''}> Bank holidays come off the allowance</label>` : ''}
      ${edit && missingNext ? `<button class="lv-btn" id="lv-copy-next">Copy ${y} allowances to ${ny} (${missingNext} not set)</button>` : ''}</div>
    <div style="overflow-x:auto"><table class="lv-table lv-2y">
    <thead><tr><th rowspan="2">Person</th><th class="yr" colspan="5">${y}</th><th class="yr nx" colspan="5">${ny}</th></tr>
      <tr>${[y, ny].map(yr => `<th class="rt${yr === ny ? ' nx' : ''}">Contract</th><th>Pro-rata from</th><th class="rt">Entitlement</th><th class="rt">Used</th><th class="rt">Left</th>`).join('')}</tr></thead>
    <tbody>${ppl.map(email => `<tr><td><span class="lv-person">${avatar(email)}${esc(nameFor(email))}</span></td>${cells(email, y)}${cells(email, ny)}</tr>`).join('')}</tbody></table></div>`;
}

// Save contract days / start date for one person and year (approver only).
async function saveAllowance(email, year) {
  if (!isApprover()) return;
  const y = Number(year), q = f => document.querySelector(`[data-email="${CSS.escape(email)}"][data-year="${y}"][data-f="${f}"]`);
  const cRaw = String(q('contract')?.value ?? '').trim(), from = q('from')?.value || null;
  const contract = cRaw === '' ? null : Math.max(0, Math.round(Number(cRaw) * 2) / 2);
  const pr = proRata(contract, from, y);
  const others = () => state.leaveAllowances.filter(a => !(lc(a.user_email) === lc(email) && Number(a.year) === y));
  setSyncDot('busy');
  let error, row = null;
  if (!pr) {
    ({ error } = await sb.from('leave_allowances').delete().eq('user_email', email).eq('year', y));
    if (!error) state.leaveAllowances = others();
  } else {
    row = { user_email: email, year: y, days: pr.days, contract_days: contract, start_date: from && from.slice(0, 4) === String(y) ? from : null, updated_at: new Date().toISOString() };
    ({ error } = await sb.from('leave_allowances').upsert(row));
    if (!error) state.leaveAllowances = [...others(), row];
  }
  if (error) { setSyncDot('err'); showToast(/contract_days|start_date/.test(error.message) ? 'Run migration 0027 first' : 'Could not save: ' + error.message); return; }
  setSyncDot('ok');
  showToast(`Saved · ${nameFor(email)}: ${row ? `${fmtDays(row.days)} days in ${y}${pr.raw != null ? ' (pro-rata)' : ''}` : `not set for ${y}`}`);
  renderLeaveUI();
}

// Approver switch: do bank holidays come off the allowance?
async function setBhRule(on) {
  if (!isApprover()) return;
  const value = on ? 'on' : 'off', before = state.settings.leave_bh_deduct;
  state.settings.leave_bh_deduct = value;
  setSyncDot('busy');
  const { error } = await sb.from('app_settings').upsert({ key: 'leave_bh_deduct', value, updated_at: new Date().toISOString() });
  if (error) { state.settings.leave_bh_deduct = before; setSyncDot('err'); showToast('Could not save: ' + error.message); renderLeaveUI(); return; }
  setSyncDot('ok'); showToast(on ? 'Bank holidays now come off the allowance' : 'Bank holidays no longer come off the allowance');
  renderLeaveUI();
}

// Fill next year's blanks from this year (contract days, no pro-rata). Never overwrites.
async function copyToNextYear() {
  if (!isApprover()) return;
  const y = state.leaveYear, ny = y + 1;
  const rows = people().map(e => ({ e, a: allowanceRow(e, y) })).filter(x => x.a && !allowanceRow(x.e, ny))
    .map(({ e, a }) => { const c = a.contract_days != null ? Number(a.contract_days) : Number(a.days);
      return { user_email: e, year: ny, days: c, contract_days: c, start_date: null, updated_at: new Date().toISOString() }; });
  if (!rows.length) return;
  setSyncDot('busy');
  const { error } = await sb.from('leave_allowances').upsert(rows);
  if (error) { setSyncDot('err'); showToast('Could not copy: ' + error.message); return; }
  state.leaveAllowances = [...state.leaveAllowances, ...rows];
  setSyncDot('ok'); showToast(`Copied ${rows.length} ${rows.length === 1 ? 'allowance' : 'allowances'} to ${ny}`);
  renderLeaveUI();
}

/* ── Everything leave-related on screen ────────────────── */
/* ── Avatar badge: leave news since you last looked ────── */
// Your own requests decided since you last opened My leave, plus (for the
// approver) requests waiting for a decision. "Last looked" is per browser.
const seenKey = () => `grantt_leave_seen_${me()}`;
function seenAt() {
  try {
    let v = localStorage.getItem(seenKey());
    if (!v && me()) { v = new Date().toISOString(); localStorage.setItem(seenKey(), v); }   // first run: nothing old counts as news
    return v ? new Date(v) : new Date();
  } catch (e) { return new Date(); }
}
function markSeen() { try { localStorage.setItem(seenKey(), new Date().toISOString()); } catch (e) { /* private mode */ } }
function leaveNews() {
  const seen = seenAt();
  return state.leaveRequests.filter(r => lc(r.user_email) === me() && (r.status === 'approved' || r.status === 'denied')
    && r.decided_at && new Date(r.decided_at) > seen && lc(r.decided_by) !== me());
}
function renderAvatarBadge() {
  const b = $('av-badge'); if (!b || !me()) return;
  const news = leaveNews().length;
  const waiting = isApprover() ? state.leaveRequests.filter(r => r.status === 'pending' && lc(r.user_email) !== me()).length : 0;
  const n = news + waiting;
  if (n && b.textContent !== String(n)) { b.classList.remove('pop'); void b.offsetWidth; b.classList.add('pop'); }
  b.textContent = n > 9 ? '9+' : String(n); b.hidden = !n;
  b.title = [news && `${news} leave ${news === 1 ? 'update' : 'updates'}`, waiting && `${waiting} leave ${waiting === 1 ? 'request' : 'requests'} to review`].filter(Boolean).join(' · ');
  const mine = $('btn-leave-mine');
  if (mine && news) mine.textContent = `🗓️ My leave · ${news} new`;
}

export function renderLeaveUI() {
  renderMenu();
  renderAvatarBadge();
  const tab = $('vtab-leave');
  if (tab) {
    tab.style.display = isHR() ? '' : 'none';
    const n = state.leaveRequests.filter(r => r.status === 'pending').length;
    const b = $('leave-badge'); b.textContent = n; b.style.display = isApprover() && n ? 'inline-flex' : 'none';
  }
  if (state.currentView === 'leave') renderLeaveView();
  if ($('myleave-modal')?.classList.contains('open')) renderMine();
  if ($('leave-modal')?.classList.contains('open')) renderCalc();
}
export { renderLeaveView };

// Live updates: keep state in step and give a gentle nudge where it helps.
export function onLeaveRequestChange(p) {
  const { eventType: ev, new: n, old: o } = p;
  if (ev === 'DELETE') { state.leaveRequests = state.leaveRequests.filter(r => r.id !== o.id); renderLeaveUI(); return; }
  const prev = state.leaveRequests.find(r => r.id === n.id), was = prev?.status;
  if (prev) Object.assign(prev, n); else state.leaveRequests.push(n);
  const mineReq = lc(n.user_email) === me(), byOther = lc(n.decided_by) !== me();
  if (mineReq && was === 'pending' && n.status === 'approved' && byOther) {
    const y = Number(n.start.slice(0, 4)), s = leaveSummary(me(), y);
    showToast(`✅ Your leave for ${fmtRange(n.start, n.end)} was approved${s.allowance != null ? ` · ${fmtDays(s.left)} ${dayWord(s.left)} left in ${y}` : ''}`);
    levelUp(); confetti({ emoji: ['🌴', '☀️', '🎉'] });   // only while GRANTT is open
  }
  if (mineReq && was === 'pending' && n.status === 'denied' && byOther) showToast(`Your leave for ${fmtRange(n.start, n.end)} was declined`);
  if (isApprover() && !mineReq && ev === 'INSERT') showToast(`🌴 New leave request from ${nameFor(n.user_email)}`);
  if (isApprover() && !mineReq && was && was !== 'cancelled' && n.status === 'cancelled') showToast(`${nameFor(n.user_email)} cancelled leave for ${fmtRange(n.start, n.end)}`);
  renderLeaveUI();
}
export function onAbsenceChange(p) {
  const { eventType: ev, new: n, old: o } = p;
  if (ev === 'DELETE') state.absences = state.absences.filter(a => a.id !== o.id);
  else { const i = state.absences.findIndex(a => a.id === n.id); if (i >= 0) state.absences[i] = n; else state.absences.push(n); }
  renderLeaveUI();
}

export function onLeaveAllowanceChange(p) {
  const { eventType: ev, new: n, old: o } = p;
  const k = a => `${lc(a.user_email)}|${a.year}`;
  const key = k(ev === 'DELETE' ? o : n);
  state.leaveAllowances = state.leaveAllowances.filter(a => k(a) !== key);
  if (ev !== 'DELETE') state.leaveAllowances.push(n);
  renderLeaveUI();
}

export function initLeave() {
  if (!$('leave-modal')) return;
  loadBankHolidays().then(() => renderLeaveUI());
  $('btn-leave-req').addEventListener('click', openRequest);
  $('btn-leave-mine').addEventListener('click', openMine);
  ['lr-start', 'lr-end', 'lr-sh', 'lr-eh'].forEach(id => $(id).addEventListener('change', () => {
    if (id === 'lr-start' && (!$('lr-end').value || $('lr-end').value < $('lr-start').value)) $('lr-end').value = $('lr-start').value;
    renderCalc();
  }));
  $('lr-send').addEventListener('click', sendRequest);
  $('lr-x').addEventListener('click', closeRequest);
  $('lr-cancel').addEventListener('click', closeRequest);
  $('ml-x').addEventListener('click', () => $('myleave-modal').classList.remove('open'));
  $('ml-close').addEventListener('click', () => $('myleave-modal').classList.remove('open'));
  $('ml-new').addEventListener('click', openRequest);
  $('ml-body').addEventListener('click', e => {
    const b = e.target.closest('[data-cancel]'); if (b) return cancelMine(b.dataset.cancel);
    const mt = e.target.closest('[data-mt]'); if (mt) { mineTab = mt.dataset.mt; return renderMine(); }
    const my = e.target.closest('[data-my]'); if (my) { mineYear = Number(my.dataset.my); return renderMine(); }
    const q = e.target.closest('[data-rerequest]');
    if (q) { const r = state.leaveRequests.find(x => x.id === q.dataset.rerequest); if (r) openRequest({ start: r.proposal_start, end: r.proposal_end || r.proposal_start, note: r.note || '' }); }
  });
  $('dc-send').addEventListener('click', sendDecline);
  initRecords();
  $('dc-cancel').addEventListener('click', () => $('decline-modal').classList.remove('open'));
  $('dc-x').addEventListener('click', () => $('decline-modal').classList.remove('open'));
  // Approver view
  $$('.lv-tab').forEach(b => b.addEventListener('click', () => { state.leaveTab = b.dataset.lt; renderLeaveView(); }));
  const ys = $('lv-year'), y = thisYear();
  ys.innerHTML = [y - 1, y, y + 1].map(v => `<option value="${v}"${v === y ? ' selected' : ''}>${v}</option>`).join('');
  ys.addEventListener('change', () => { state.leaveYear = +ys.value; renderLeaveView(); });
  $('lv-body').addEventListener('click', e => {
    const a = e.target.closest('[data-approve]'); if (a) return approve(a.dataset.approve);
    const d = e.target.closest('[data-deny]'); if (d) return decline(d.dataset.deny);
  });
  $('lv-body').addEventListener('change', e => {
    const t = e.target;
    if (t.classList.contains('lv-allow') || t.classList.contains('lv-from')) saveAllowance(t.dataset.email, t.dataset.year);
    if (t.id === 'lv-bh') setBhRule(t.checked);
  });
  $('lv-body').addEventListener('click', e => { if (e.target.closest('#lv-copy-next')) copyToNextYear(); });
}
